/// <amd-module name="VSS/Features/PlatformUI/ContributedItemUtil" />
import { IObservable, IObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, setRemoteSerializationSettings } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

export function getContributedItems<TContribution>(
    pageContext: IVssPageContext,
    targetContributionId: string,
    contributionPropertyName: string | undefined,
    itemContributionType: string
): Array<{ id: string; contribution: TContribution }> {
    const items: Array<{ id: string; contribution: TContribution }> = [];
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

    // Get the ids of all the contributions that we should add
    const itemContributionIds: string[] = [];
    const itemsByIdHash: { [itemId: string]: boolean } = {};

    // Get the ids of contributions that directly target the given targetContributionId
    const children = contributionService.getContributionChildren(targetContributionId, itemContributionType);
    if (children) {
        for (const child of children) {
            itemsByIdHash[child] = true;
            itemContributionIds.push(child);
        }
    }

    // Get all contributions of this type, and find ones that have a property (not target) equal to our targetContributionId
    if (contributionPropertyName) {
        const itemsByType = contributionService.getContributionsByType<any>(itemContributionType);
        if (itemsByType) {
            for (const itemId in itemsByType) {
                if (!itemsByIdHash[itemId]) {
                    const contribution = itemsByType[itemId];
                    if (contribution && contribution[contributionPropertyName] === targetContributionId) {
                        itemContributionIds.push(itemId);
                    }
                }
            }
        }
    }

    // Fetch the contribution for each item
    for (const id of itemContributionIds) {
        const contribution = contributionService.getContribution<TContribution>(id);
        if (contribution) {
            items.push({ id, contribution });
        }
    }

    return items;
}

/**
 * Take an observable that was returned from an external source (iframe) and wrap its
 * subscribe method so that the subscriber we pass to it is allowed to be proxied across
 * the iframe boundary.
 *
 * @param observableLike Possibly-observable item that was returned from an external source
 */
export function wrapExternalObservable<T>(observableLike: T): T {
    if (observableLike) {
        const observable = (observableLike as any) as IObservable<T>;
        const originalSubscribe = observable.subscribe;
        if (typeof originalSubscribe === "function") {
            observable.subscribe = (observer: (value: T, action?: string) => void, action?: string): (value: T, action?: string) => void => {
                const innerObserver = (value: T, action?: string): void => {
                    // For an external ObservableValue, update the 'value' property
                    const observableValue = (observableLike as any) as IObservableValue<any>;
                    if (action === "set" && typeof observableValue.value !== "undefined") {
                        observableValue.value = value;
                    }

                    observer(value, action);
                };
                setRemoteSerializationSettings(innerObserver, { proxyFunctions: true });
                return originalSubscribe.call(observable, innerObserver, action);
            };
        }
    }

    return observableLike;
}
